import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import {
  getThermometerBoard,
  validateThermometerSolution,
  getThermometerSolution,
  getThermometerHint,
  type ThermometerBoard,
  type Thermometer
} from '../../services/thermometersService';

import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';
import '../../styles/thermometers.css';

// Estados de celda
type CellState = 'cell-off' | 'cell-on' | 'cell-x';

// Estado del tablero
interface BoardState {
  [row: number]: {
    [col: number]: number; // 0 = vacío/marcado, 1 = lleno
  };
}

// Historial para deshacer/rehacer
interface HistoryEntry {
  cellStates: Map<string, CellState>;
  moveCount: number;
}

export default function ThermometersGame() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Obtener dificultad de los parámetros de URL
  const dificultad_id = useMemo(() => {
    const diffParam = searchParams.get('dificultad');
    return diffParam ? parseInt(diffParam) : 1;
  }, [searchParams]);

  const { saveProgress: saveGameProgress } = useGameProgress('termometros', dificultad_id);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();

  // Estados del juego
  const [board, setBoard] = useState<ThermometerBoard | null>(null);
  const [gameStartTime] = useState<number>(Date.now());
  const [moveCount, setMoveCount] = useState<number>(0);
  const [cellStates, setCellStates] = useState<Map<string, CellState>>(new Map());
  const [puzzleSolved, setPuzzleSolved] = useState<boolean>(false);
  const [victoryModalOpen, setVictoryModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Historial para deshacer/rehacer
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number>(-1);

  // Modo asistido
  const [modoAsistido, setModoAsistido] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [solution, setSolution] = useState<number[][] | null>(null);

  // Medición del contenedor para tamaño adaptable
  const boardAreaRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  // Dificultades disponibles
  const difficulties = [
    { id: 1, nombre: 'Neurona', icono: '🧠', color_hex: '#22c55e', orden: 1 },
    { id: 2, nombre: 'Sinapsis', icono: '⚡', color_hex: '#3b82f6', orden: 2 },
    { id: 3, nombre: 'Cerebro', icono: '🧩', color_hex: '#a855f7', orden: 3 },
    { id: 4, nombre: 'Genio', icono: '💡', color_hex: '#ef4444', orden: 4 },
    { id: 5, nombre: 'Einstein', icono: '🎓', color_hex: '#f97316', orden: 5 },
  ];

  // Medir el contenedor del tablero (se ejecuta cuando termina de cargar)
  useEffect(() => {
    if (!boardAreaRef.current) return;
    const el = boardAreaRef.current;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isLoading]);

  // Cargar tablero cuando cambia la dificultad
  useEffect(() => {
    loadBoard();
  }, [dificultad_id]);

  // Guardar estado inicial en historial cuando se carga el tablero
  useEffect(() => {
    if (board && cellStates.size > 0 && history.length === 0) {
      saveToHistory();
    }
  }, [board, cellStates]);

  /**
   * Cargar tablero desde el backend
   */
  async function loadBoard(forceNew = false) {
    try {
      setIsLoading(true);
      const tableroIdParam = !forceNew ? searchParams.get('tableroId') : null;
      const tableroId = tableroIdParam ? parseInt(tableroIdParam) : undefined;
      const data = await getThermometerBoard(tableroId ? undefined : dificultad_id, tableroId);
      setBoard(data);
      setSearchParams(p => { p.set('tableroId', data.id.toString()); return p; }, { replace: true });
      
      // Inicializar estados de celdas (todas off)
      const initialStates = new Map<string, CellState>();
      data.parsed.thermometers.forEach((therm) => {
        therm.cells.forEach((cell) => {
          const key = `${cell.row}-${cell.col}`;
          initialStates.set(key, 'cell-off');
        });
      });
      
      setCellStates(initialStates);
      setPuzzleSolved(false);
      setVictoryModalOpen(false);
      setMoveCount(0);
      setHistory([]);
      setHistoryIndex(-1);
      setSolution(null);
    } catch (error) {
      console.error('❌ Error cargando tablero:', error);
      toast.error('Error cargando el tablero');
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * Guardar estado en historial
   */
  function saveToHistory() {
    // Eliminar estados futuros si estamos en medio del historial
    const newHistory = history.slice(0, historyIndex + 1);
    
    newHistory.push({
      cellStates: new Map(cellStates),
      moveCount
    });
    
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }

  /**
   * Deshacer movimiento
   */
  function undo() {
    if (historyIndex <= 0 || puzzleSolved) return;
    
    const prevIndex = historyIndex - 1;
    const prevState = history[prevIndex];
    
    setCellStates(new Map(prevState.cellStates));
    setMoveCount(prevState.moveCount);
    setHistoryIndex(prevIndex);
  }

  /**
   * Rehacer movimiento
   */
  function redo() {
    if (historyIndex >= history.length - 1 || puzzleSolved) return;
    
    const nextIndex = historyIndex + 1;
    const nextState = history[nextIndex];
    
    setCellStates(new Map(nextState.cellStates));
    setMoveCount(nextState.moveCount);
    setHistoryIndex(nextIndex);
  }

  /**
   * Obtener estado actual del tablero para validación
   */
  function getCurrentState(): BoardState {
    const state: BoardState = {};

    cellStates.forEach((cellState, key) => {
      const [rowStr, colStr] = key.split('-');
      const row = parseInt(rowStr);
      const col = parseInt(colStr);

      if (!state[row]) state[row] = {};

      state[row][col] = cellState === 'cell-on' ? 1 : 0;
    });

    return state;
  }

  /**
   * Guardar progreso
   */
  async function saveProgress(tiempo: number, movimientos: number) {
    if (!board) return;

    try {
      // Guardar progreso normal (siempre)
      await saveGameProgress({
        tablero_id: board.id,
        tiempo,
        movimientos,
        completado: true,
      });

      // Si viene de una sesión, guardar también en la sesión
      await saveSessionResult({
        tiempo,
        movimientos,
        completado: true,
        datos_juego: {
          tablero_id: board.id,
          size: board.size,
        },
      });
    } catch (error) {
      console.error('❌ Error al guardar progreso:', error);
    }
  }


  /**
   * Manejar click en celda
   */
  async function handleCellClick(row: number, col: number) {
    if (puzzleSolved) return;
    
    const key = `${row}-${col}`;
    const currentState = cellStates.get(key) || 'cell-off';
    
    let newState: CellState;
    let newMoveCount = moveCount;
    
    if (currentState === 'cell-off') {
      newState = 'cell-on';
      newMoveCount = moveCount + 1;
      setMoveCount(newMoveCount);
    } else if (currentState === 'cell-on') {
      newState = 'cell-x';
    } else {
      newState = 'cell-off';
    }
    
    const newStates = new Map(cellStates);
    newStates.set(key, newState);
    setCellStates(newStates);
    
    // Validar con el nuevo estado
    if (!board) return;
    
    // Construir estado para validación con el cambio actual
    const state: BoardState = {};
    newStates.forEach((cellState, cellKey) => {
      const [rowStr, colStr] = cellKey.split('-');
      const r = parseInt(rowStr);
      const c = parseInt(colStr);
      
      if (!state[r]) state[r] = {};
      state[r][c] = cellState === 'cell-on' ? 1 : 0;
    });
    
    try {
      const validation = await validateThermometerSolution(board.task, state);

      if (validation.valid) {
        setPuzzleSolved(true);
        const tiempoTotal = Math.floor((Date.now() - gameStartTime) / 1000);
        setVictoryModalOpen(true);


        // Guardar progreso
        await saveProgress(tiempoTotal, newMoveCount);
      }
    } catch (error) {
      console.error('Error validando solución:', error);
    }
    
    // Guardar en historial después
    setTimeout(() => {
      saveToHistory();
    }, 50);
  }

  /**
   * Reiniciar juego
   */
  function resetGame() {
    if (puzzleSolved) return;
    
    const initialStates = new Map<string, CellState>();
    if (board) {
      board.parsed.thermometers.forEach((therm) => {
        therm.cells.forEach((cell) => {
          const key = `${cell.row}-${cell.col}`;
          initialStates.set(key, 'cell-off');
        });
      });
    }
    
    setCellStates(initialStates);
    setMoveCount(0);
    setHistory([]);
    setHistoryIndex(-1);
    setPuzzleSolved(false);
  }

  /**
   * Pista: revela una celda incorrecta o vacía
   */
  async function handleHint() {
    if (!board) return;
    try {
      const stateObj: { [row: number]: { [col: number]: number } } = {};
      cellStates.forEach((st, key) => {
        const [r, c] = key.split('-').map(Number);
        if (!stateObj[r]) stateObj[r] = {};
        stateObj[r][c] = st === 'cell-on' ? 1 : 0;
      });
      const hint = await getThermometerHint(board.task, stateObj);
      if (hint.row === null) {
        toast.info('No hay más pistas disponibles');
        return;
      }
      const key = `${hint.row}-${hint.col}`;
      setCellStates(prev => {
        const next = new Map(prev);
        next.set(key, hint.value === 1 ? 'cell-on' : 'cell-off');
        return next;
      });
      setMoveCount(m => m + 1);
    } catch {
      toast.error('Error obteniendo pista');
    }
  }

  /**
   * Ver solución completa
   */
  async function handleShowSolution() {
    if (!board) return;
    if (!window.confirm('¿Estás seguro de que quieres ver la solución? Esto no guardará tu progreso.')) return;
    try {
      let sol = solution;
      if (!sol) {
        sol = await getThermometerSolution(board.task);
        setSolution(sol);
      }
      const next = new Map<string, CellState>();
      board.parsed!.thermometers.forEach(therm => {
        therm.cells.forEach(cell => {
          const key = `${cell.row}-${cell.col}`;
          next.set(key, sol![cell.row][cell.col] === 1 ? 'cell-on' : 'cell-off');
        });
      });
      setCellStates(next);
      toast.info('Solución revelada. No se guardará este progreso.');
    } catch {
      toast.error('Error obteniendo solución');
    }
  }

  /**
   * Nuevo tablero
   */
  function newBoard() {
    loadBoard(true);
  }

  /**
   * Modo asistido: calcular errores según reglas del juego
   */
  function calcularErrores(
    parsed: NonNullable<typeof board>['parsed'],
    states: Map<string, CellState>
  ): { therms: Set<number>; vcols: Set<number>; hrows: Set<number> } {
    const therms = new Set<number>();
    const vcols  = new Set<number>();
    const hrows  = new Set<number>();

    // 1. Termómetros con hueco (celda ON después de OFF desde el bulbo)
    for (const therm of parsed.thermometers) {
      let foundOff = false;
      for (const cell of therm.cells) {
        const s = states.get(`${cell.row}-${cell.col}`) ?? 'cell-off';
        if (s === 'cell-on' && foundOff) { therms.add(therm.index); break; }
        if (s !== 'cell-on') foundOff = true;
      }
    }

    // 2. Contadores de columna
    for (let col = 0; col < parsed.width; col++) {
      let filled = 0, available = 0;
      for (let row = 0; row < parsed.height; row++) {
        const s = states.get(`${row}-${col}`) ?? 'cell-off';
        if (s === 'cell-on')  filled++;
        else if (s === 'cell-off') available++;
      }
      const target = parsed.verticalCounters[col];
      if (filled > target || filled + available < target) vcols.add(col);
    }

    // 3. Contadores de fila
    for (let row = 0; row < parsed.height; row++) {
      let filled = 0, available = 0;
      for (let col = 0; col < parsed.width; col++) {
        const s = states.get(`${row}-${col}`) ?? 'cell-off';
        if (s === 'cell-on')  filled++;
        else if (s === 'cell-off') available++;
      }
      const target = parsed.horizontalCounters[row];
      if (filled > target || filled + available < target) hrows.add(row);
    }

    return { therms, vcols, hrows };
  }

  /**
   * Determinar clases CSS para una celda (EXACTO del original PHP)
   */
  function getCellClasses(therm: Thermometer, cellIndex: number, hasError = false): string {
    const classes = ['cell', 'selectable'];
    const totalCells = therm.cells.length;
    const curr = therm.cells[cellIndex];
    
    if (cellIndex === 0) {
      // BULBO (primera celda)
      classes.push('start');
      
      if (therm.cells[cellIndex + 1]) {
        const next = therm.cells[cellIndex + 1];
        const colDiff = next.col - curr.col;
        const rowDiff = next.row - curr.row;
        
        if (colDiff === 1) classes.push('r1');
        else if (rowDiff === 1) classes.push('r2');
        else if (colDiff === -1) classes.push('r3');
      }
    } else if (cellIndex === totalCells - 1) {
      // EXTREMO (última celda)
      classes.push('end');
      
      const prev = therm.cells[cellIndex - 1];
      const colDiff = prev.col - curr.col;
      const rowDiff = prev.row - curr.row;
      
      if (colDiff === 1) classes.push('r1');
      else if (rowDiff === 1) classes.push('r2');
      else if (colDiff === -1) classes.push('r3');
    } else {
      // MEDIO (puede ser straight o curve)
      const prev = therm.cells[cellIndex - 1];
      const next = therm.cells[cellIndex + 1];
      
      if (prev.row === next.row) {
        // Horizontal straight
        classes.push('straight');
        classes.push('r1');
      } else if (prev.col === next.col) {
        // Vertical straight
        classes.push('straight');
      } else {
        // CURVE
        classes.push('curve');
        
        if ((next.col - curr.col === 1 && prev.row - curr.row === 1) ||
            (prev.col - curr.col === 1 && next.row - curr.row === 1)) {
          classes.push('r1');
        } else if ((next.col - curr.col === -1 && prev.row - curr.row === 1) ||
                   (prev.col - curr.col === -1 && next.row - curr.row === 1)) {
          classes.push('r2');
        } else if ((next.col - curr.col === -1 && prev.row - curr.row === -1) ||
                   (prev.col - curr.col === -1 && next.row - curr.row === -1)) {
          classes.push('r3');
        }
      }
    }
    
    // Añadir estado de la celda
    const key = `${curr.row}-${curr.col}`;
    const state = cellStates.get(key) || 'cell-off';
    classes.push(state);

    if (hasError) classes.push('cell-error');

    return classes.join(' ');
  }

  // Calcular dificultad actual antes de los returns
  const dificultad_actual = difficulties.find(d => d.orden === dificultad_id);

  if (isLoading || !board) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#667eea] to-[#764ba2] flex items-center justify-center">
        <div className="text-white text-2xl">Cargando...</div>
      </div>
    );
  }

  const parsed = board.parsed;
  const cols = parsed.width + 1;
  const rows = parsed.height + 1;
  const cellPx = containerSize.width > 0 && containerSize.height > 0
    ? Math.min(containerSize.width / cols, containerSize.height / rows)
    : 0;
  const boardPx = cellPx > 0 ? { width: cellPx * cols, height: cellPx * rows } : null;

  const difficultyOptions = [
    { orden: 1, nombre: 'Neurona', icono: '🧠' },
    { orden: 2, nombre: 'Sinapsis', icono: '⚡' },
    { orden: 3, nombre: 'Cerebro', icono: '🧩' },
    { orden: 4, nombre: 'Genio', icono: '💡' },
    { orden: 5, nombre: 'Einstein', icono: '🎓' },
  ];

  return (
    <div className="h-full bg-gradient-to-br from-[#667eea] to-[#764ba2] flex flex-col">
      {isInSession && sessionType && <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />}

      {/* Compact top bar */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">🌡️ Termometros</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {difficultyOptions.map(d => (
            <button
              key={d.orden}
              onClick={() => { window.location.href = `?dificultad=${d.orden}`; }}
              className={`px-2 py-0.5 rounded text-xs font-semibold transition-all ${
                dificultad_id === d.orden
                  ? 'bg-white text-purple-700'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {d.icono} {d.nombre}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <div className="flex gap-1 items-center">
          {/* Undo */}
          <button onClick={undo} disabled={historyIndex <= 0} title="Deshacer"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
          </button>
          {/* Redo */}
          <button onClick={redo} disabled={historyIndex >= history.length - 1} title="Rehacer"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 10H11a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
            </svg>
          </button>
          {/* Modo asistido */}
          <button onClick={() => setModoAsistido(v => !v)} title={modoAsistido ? 'Desactivar modo asistido' : 'Activar modo asistido'}
            className={`p-1.5 rounded transition-all text-white ${modoAsistido ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-white/20 hover:bg-white/30'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
          </button>
          {/* Reset */}
          <button onClick={resetGame} disabled={puzzleSolved} title="Reiniciar"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          <button onClick={handleHint} disabled={puzzleSolved} title="Pista"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m1.343-5.657l-.707-.707M12 21v-1M5.636 5.636l.707.707M12 13a3 3 0 100-6 3 3 0 000 6z" />
            </svg>
          </button>
          <button onClick={handleShowSolution} disabled={puzzleSolved} title="Ver solución"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          {/* New board */}
          <button onClick={newBoard} title="Nuevo tablero"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </button>
          <button onClick={() => setShowHowToPlay(true)} title="Cómo se juega"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </button>
        </div>
      </div>

      {/* Board Area */}
      <div ref={boardAreaRef} className="flex-1 min-h-0 flex items-center justify-center p-2 overflow-hidden">
          <div
            className="thermometers-board"
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${cols}, 1fr)`,
              gridTemplateRows: `repeat(${rows}, 1fr)`,
              gap: 0,
              padding: 0,
              ...(boardPx ? { width: boardPx.width, height: boardPx.height } : {}),
            }}
          >
            {(() => {
              const errores = modoAsistido ? calcularErrores(parsed, cellStates) : null;
              return (<>
                {parsed.verticalCounters.map((count, col) => (
                  <div
                    key={`v-counter-${col}`}
                    className={`cell task v${errores?.vcols.has(col) ? ' cell-error' : ''}`}
                    style={{ gridColumn: col + 2, gridRow: 1 }}
                  >
                    {count}
                  </div>
                ))}

                {parsed.horizontalCounters.map((count, row) => (
                  <div
                    key={`h-counter-${row}`}
                    className={`cell task h${errores?.hrows.has(row) ? ' cell-error' : ''}`}
                    style={{ gridColumn: 1, gridRow: row + 2 }}
                  >
                    {count}
                  </div>
                ))}

                {parsed.thermometers.map((therm) =>
                  therm.cells.map((cell, cellIndex) => (
                    <div
                      key={`therm-${therm.index}-cell-${cellIndex}`}
                      className={getCellClasses(therm, cellIndex, errores?.therms.has(therm.index) ?? false)}
                      style={{ gridColumn: cell.col + 2, gridRow: cell.row + 2 }}
                      data-row={cell.row}
                      data-col={cell.col}
                      onClick={() => handleCellClick(cell.row, cell.col)}
                    >
                      <div className="inner"></div>
                    </div>
                  ))
                )}
              </>);
            })()}
          </div>
      </div>

      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
        gameTitle="Thermometers"
        gameIcon="🌡️"
        objective="Rellena los termómetros con mercurio de forma que los contadores de filas y columnas sean correctos."
        rules={[
          'Los termómetros se rellenan siempre desde el bulbo (extremo redondeado) hacia el extremo abierto, sin saltar celdas',
          'Los números en los bordes indican cuántas celdas rellenas hay en esa fila o columna',
          'Puedes rellenar parcialmente un termómetro, pero siempre de forma continua desde el bulbo',
        ]}
        howToPlay="Toca una celda del termómetro para cambiar su estado. El mercurio se extiende automáticamente desde el bulbo hasta la celda que toques."
        author="Nikoli"
      />

      <VictoryModal
        isOpen={victoryModalOpen}
        onClose={() => setVictoryModalOpen(false)}
        stats={{
          tiempo: Math.floor((Date.now() - gameStartTime) / 1000),
          movimientos: moveCount
        }}
      />
    </div>
  );
}
